import { useEffect, useRef, useState } from 'react';
import '@xyflow/react/dist/style.css';
import { ReactFlowProvider, useReactFlow } from '@xyflow/react';
import { PeopleAltOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import EmployeeModal from './components/employeeModal';
import { buildATree, expandPathToNode } from './utils/treeHelper';
import { connect } from 'react-redux';
import createCachedSelector from 're-reselect';
import {
    createFetchPaginatedDataset,
    datasetLoadingSelector,
    entitySelector
} from '@libs/datasets';

import { EMPLOYEES } from './redux.datasets';
import { DOMAIN_NAME } from './common';
import AllEmployeeListModal from './components/allEmployeeListModal';
import ManagerialTreeFlow from './components/managerialTreeFlow';

const defaultProps = {
    entity: EMPLOYEES,
    domain: DOMAIN_NAME,
    loading: true
};

const dataSelector = createCachedSelector(entitySelector, (resp) => {
    return resp && resp.data
        ? resp.data.map((obj) =>
              (({
                  user,
                  branch = {},
                  designation = {},
                  department = {},
                  ...rest
              }) => ({
                  ...rest,
                  email: user?.email,
                  branchName: branch?.name,
                  designationName: designation?.name,
                  departmentName: department?.name,
                  userId: user?.email
              }))(obj)
          )
        : [];
})((state, domain, entity) => `@data-${domain}-${entity}`);

const totalRowsSelector = createCachedSelector(entitySelector, (resp) =>
    resp.total ? parseInt(resp.total) : 0
)((state, domain, entity) => `@data-${domain}-${entity}`);

const mapState = (state, { domain, entity }) => ({
    data: dataSelector(state, domain, entity),
    loading: datasetLoadingSelector(state, domain, entity),
    totalRows: totalRowsSelector(state, domain, entity)
});

const mapDispatch = (dispatch, { entity }) => ({
    fetchDataset: ({
        offset = '',
        limit = 12,
        shouldAppend = false,
        searchTerm
    }) => {
        return dispatch(
            createFetchPaginatedDataset({
                entity: entity,
                domain: DOMAIN_NAME
            })({
                limit,
                offset,
                shouldAppend,
                searchTerm
            })
        );
    }
});

const ManagerialTreeContent = ({ fetchDataset, data, totalRows = 0 }) => {
    const [tree, setTree] = useState([]);
    const treeRef = useRef(tree);
    const { fitView } = useReactFlow();

    useEffect(() => {
        if (data && data.length > 0) {
            const builtTree = buildATree(data);
            setTree(builtTree);
            treeRef.current = builtTree;
        }
    }, [data]);

    useEffect(() => {
        fetchDataset({ offset: '', limit: -1 });
    }, [fetchDataset]);

    const [openSingleEmpDrawer, setOpenSingleEmpDrawer] = useState(false);
    const [openAllEmpDrawer, setOpenAllEmpDrawer] = useState(false);
    const [selectedNodeData, setSelectedNodeData] = useState(null);
    

    const toggleDrawer = (newOpen) => () => {
        setOpenSingleEmpDrawer(newOpen);
    };

    const toggleAllEmpDrawer = (drawerStatus) => () => {
        setOpenAllEmpDrawer(drawerStatus);
    };

    const onNodeClickHandler = (id) => {
        setSelectedNodeData(id);
        setOpenSingleEmpDrawer(!openSingleEmpDrawer);
        // expand path to node and trigger re-render
        const expandedTree = expandPathToNode([...treeRef.current], id);
        setTree(expandedTree);
        treeRef.current = expandedTree; // update the ref!

        setTimeout(() => {
            fitView({
                nodes: [{ id: id }],
                duration: 300,
                maxZoom: 1,
                minZoom: 0.1
            });
        }, 100);
    };

    const afterManagerUpdateHandler = () => {
        fetchDataset({ offset: '', limit: -1 });
    };

    const isLoading = !data || data.length === 0;

    if (isLoading) return <Box sx={{ p: 4 }}>Loading...</Box>;

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', position: 'relative' }}>
            <PeopleAltOutlined
                onClick={toggleAllEmpDrawer(true)}
                fontSize='large'
                sx={(t) => ({
                    position: 'absolute',
                    right: 40,
                    zIndex: 999,
                    cursor: 'pointer',
                    bgcolor: t.palette.primary.main,
                    color: t.palette.primary.contrastText,
                    p: 1,
                    borderRadius: '50%',
                    '&:hover': {
                        bgcolor: t.palette.primary.dark
                    }
                })}
                aria-label='open-all-employees'
            />

            {openAllEmpDrawer && (
                <AllEmployeeListModal
                    open={openAllEmpDrawer}
                    toggleAllEmpDrawer={toggleAllEmpDrawer}
                    empListData={data}
                    specificEmployee={onNodeClickHandler}
                />
            )}

            {openSingleEmpDrawer && (
                <EmployeeModal
                    empTree={tree}
                    toggleDrawer={toggleDrawer}
                    selectedNodeData={selectedNodeData}
                    open={openSingleEmpDrawer}
                    afterManagerUpdateHandler={afterManagerUpdateHandler}
                />
            )}

            <ManagerialTreeFlow
                onNodeClickHandler={onNodeClickHandler}
                employeeDataSets={data}
                tree={tree}
                setTree={setTree}
                treeRef={treeRef}
            />
        </Box>
    );
};

const ManagerialTree = connect(
    mapState,
    mapDispatch
)((props) => {
    return (
        <>
            <ReactFlowProvider>
                <ManagerialTreeContent {...props} />
            </ReactFlowProvider>
        </>
    );
});

ManagerialTree.defaultProps = defaultProps;

export default ManagerialTree;
